import type {
  CollaborationAuthorizationState,
  CollaborationEditorStatus,
  CollaborationLocalChangeState,
  CollaborationTransportState,
} from "@workith/shared";
import { useEffect, useRef, useState } from "react";
import type {
  CollaborationCloseClassification,
  CollaborationCloseInfo,
} from "./collaborationProvider";

/**
 * Editor state on three independent axes (KTD5).
 *
 * The bug this replaces was a single boolean: editability was `isConnected &&
 * hasSynced && ...`, so every lease renewal and every transient reconnect took
 * `contenteditable` away, which drops DOM focus, the ProseMirror selection and
 * any IME composition in flight. Transport is now status only. Editability
 * comes from the authorization axis and from one sticky fact - whether this
 * provider ever completed its first authorization and sync (R9).
 */
export interface CollaborationEditorState {
  readonly transport: CollaborationTransportState;
  /** What the surrounding app grants: the document ACL/role of this viewer. */
  readonly hasEditPermission: boolean;
  /**
   * Sticky. Set by the first successful sync of the current provider and never
   * cleared, because a reconnect resyncs the same Y.Doc rather than starting a
   * new document. Only a new provider (`reset`) starts over.
   */
  readonly hasCompletedFirstSync: boolean;
  /** Set once the server ends this room; nothing this tab sends is admitted. */
  readonly terminal: CollaborationTerminalState | null;
  readonly isComposing: boolean;
  /**
   * A terminal answer arrived while an IME composition was in flight. The
   * composition finishes into the local Y.Doc - network admission is already
   * blocked - and read-only applies when it ends, so the half-formed 한글
   * syllable is neither committed to the server nor thrown away (R9, R13).
   */
  readonly isFinishingComposition: boolean;
  readonly unsyncedCount: number;
  /** Rejected local content was captured and is offered for export (U7). */
  readonly isRecoveryAvailable: boolean;
}

export interface CollaborationTerminalState {
  readonly classification: CollaborationCloseClassification;
  readonly authorization: Extract<CollaborationAuthorizationState, "readOnly" | "revoked">;
  /** `document`: the whole document is over. `room`: only this room. */
  readonly scope: CollaborationCloseInfo["scope"];
}

export type CollaborationEditorEvent =
  /** A new provider was built; every axis starts over. */
  | { type: "reset"; canEdit: boolean }
  | { type: "connecting" }
  | { type: "synced" }
  | { type: "transportLost" }
  /** The connection never came up in time; retries continue. */
  | { type: "transportUnreachable" }
  | { type: "closed"; close: CollaborationCloseInfo }
  | { type: "permissionChanged"; canEdit: boolean }
  | { type: "unsyncedChanges"; count: number }
  | { type: "compositionStart" }
  | { type: "compositionEnd" }
  | { type: "recoveryCaptured" }
  | { type: "recoveryResolved" };

/**
 * How a terminal close lands on the authorization axis. A downgrade leaves the
 * document readable, everything else ends this client's access to the room.
 *
 * `lease-expired` maps to `revoked` and does not re-arm. That is the deliberate
 * fail-closed choice, not an oversight - see
 * `CollaborationCredentialBroker.suspend` for why a transient token-endpoint
 * outage is answered with a hard stop plus recovery rather than a retry.
 */
const TERMINAL_AUTHORIZATION: Readonly<
  Partial<Record<CollaborationCloseClassification, CollaborationTerminalState["authorization"]>>
> = {
  "authorization-downgraded": "readOnly",
  "authorization-denied": "revoked",
  "authorization-revoked": "revoked",
  "lease-expired": "revoked",
  "section-unavailable": "revoked",
  "room-limit-exceeded": "revoked",
};

export function createCollaborationEditorState(canEdit: boolean): CollaborationEditorState {
  return {
    transport: "connecting",
    hasEditPermission: canEdit,
    hasCompletedFirstSync: false,
    terminal: null,
    isComposing: false,
    isFinishingComposition: false,
    unsyncedCount: 0,
    isRecoveryAvailable: false,
  };
}

/**
 * A transport transition is meaningless once the room is terminal: the server
 * has already answered, and letting a socket event overwrite that would show a
 * revoked document as merely reconnecting.
 */
function withTransport(
  state: CollaborationEditorState,
  transport: CollaborationTransportState
): CollaborationEditorState {
  if (state.terminal || state.transport === transport) return state;
  return { ...state, transport };
}

function retryingTransport(state: CollaborationEditorState): CollaborationTransportState {
  return state.hasCompletedFirstSync ? "reconnecting" : "connecting";
}

export function collaborationEditorReducer(
  state: CollaborationEditorState,
  event: CollaborationEditorEvent
): CollaborationEditorState {
  switch (event.type) {
    case "reset":
      return createCollaborationEditorState(event.canEdit);

    case "connecting":
      return withTransport(state, retryingTransport(state));

    case "synced":
      if (state.terminal) return state;
      return { ...state, transport: "online", hasCompletedFirstSync: true };

    case "transportLost":
      return withTransport(state, retryingTransport(state));

    case "transportUnreachable":
      return withTransport(state, "offline");

    case "closed": {
      const { close } = event;
      // A client-initiated detach is not an answer about access, and a plain
      // socket close is a transport symptom that reconnecting can fix.
      if (close.classification === "detached") return state;
      const authorization = close.terminal ? TERMINAL_AUTHORIZATION[close.classification] : null;
      if (!authorization) return withTransport(state, retryingTransport(state));
      if (state.terminal) return state;

      return {
        ...state,
        terminal: { classification: close.classification, authorization, scope: close.scope },
        isFinishingComposition: state.isComposing,
      };
    }

    case "permissionChanged":
      if (state.hasEditPermission === event.canEdit) return state;
      return { ...state, hasEditPermission: event.canEdit };

    case "unsyncedChanges":
      if (state.unsyncedCount === event.count) return state;
      return { ...state, unsyncedCount: event.count };

    case "compositionStart":
      if (state.isComposing) return state;
      return { ...state, isComposing: true };

    case "compositionEnd":
      if (!state.isComposing && !state.isFinishingComposition) return state;
      return { ...state, isComposing: false, isFinishingComposition: false };

    case "recoveryCaptured":
      if (state.isRecoveryAvailable) return state;
      return { ...state, isRecoveryAvailable: true };

    case "recoveryResolved":
      if (!state.isRecoveryAvailable) return state;
      return { ...state, isRecoveryAvailable: false };
  }
}

export function collaborationAuthorizationState(
  state: CollaborationEditorState
): CollaborationAuthorizationState {
  if (state.terminal) return state.terminal.authorization;
  if (!state.hasCompletedFirstSync) return "pending";
  return state.hasEditPermission ? "editable" : "readOnly";
}

export function collaborationLocalChangeStateFrom({
  hasPendingChanges,
  transport,
  isTerminal,
}: {
  hasPendingChanges: boolean;
  transport: CollaborationTransportState;
  isTerminal: boolean;
}): CollaborationLocalChangeState {
  if (!hasPendingChanges) return "clean";
  if (isTerminal) return "rejected";
  // Only "on its way to the collaboration server" - never a persistence claim.
  return transport === "online" ? "syncing" : "pending";
}

export function collaborationLocalChangeState(
  state: CollaborationEditorState
): CollaborationLocalChangeState {
  return collaborationLocalChangeStateFrom({
    hasPendingChanges: state.unsyncedCount > 0,
    transport: state.transport,
    isTerminal: state.terminal !== null,
  });
}

export function collaborationEditorStatus(
  state: CollaborationEditorState
): CollaborationEditorStatus {
  return {
    transport: state.transport,
    authorization: collaborationAuthorizationState(state),
    localChanges: collaborationLocalChangeState(state),
  };
}

/**
 * Whether the editor accepts input right now.
 *
 * Deliberately blind to `transport`: after the first sync a disconnect leaves
 * the document editable and the local updates queue in the Y.Doc until the
 * socket returns.
 */
export function isCollaborativeEditorEditable(state: CollaborationEditorState): boolean {
  if (!state.hasCompletedFirstSync) return false;
  // A composition that started while access was still open finishes locally.
  if (state.isFinishingComposition) return true;
  return collaborationAuthorizationState(state) === "editable";
}

/**
 * Whether leaving the tab would lose work. Best effort by definition: browsers
 * only honour it after a user gesture, and it is cleared as soon as the changes
 * sync or the recovery offer is resolved.
 */
export function shouldWarnBeforeUnloadFrom({
  localChanges,
  isRecoveryAvailable,
}: {
  localChanges: CollaborationLocalChangeState;
  isRecoveryAvailable: boolean;
}): boolean {
  return localChanges === "pending" || localChanges === "rejected" || isRecoveryAvailable;
}

export function shouldWarnBeforeUnload(state: CollaborationEditorState): boolean {
  return shouldWarnBeforeUnloadFrom({
    localChanges: collaborationLocalChangeState(state),
    isRecoveryAvailable: state.isRecoveryAvailable,
  });
}

/** Maps the sectioned controller's snapshot booleans onto the transport axis. */
export function collaborationTransportState({
  isConnected,
  hasCompletedFirstSync,
  hasConnectionIssue,
}: {
  isConnected: boolean;
  hasCompletedFirstSync: boolean;
  hasConnectionIssue: boolean;
}): CollaborationTransportState {
  if (isConnected) return "online";
  if (hasConnectionIssue) return "offline";
  return hasCompletedFirstSync ? "reconnecting" : "connecting";
}

const TRANSPORT_TEXT: Readonly<Record<CollaborationTransportState, string>> = {
  connecting: "협업 서버에 연결 중",
  online: "실시간 연결됨",
  reconnecting: "연결이 끊겨 재연결 중",
  offline: "연결할 수 없어 재시도 중",
};

const LOCAL_CHANGE_TEXT: Readonly<Record<CollaborationLocalChangeState, string>> = {
  clean: "변경사항 모두 전송됨",
  // Never "저장됨": the server persists on a debounce, so a client-side sync is
  // not a database acknowledgement (ADR-022).
  syncing: "변경사항 전송 중",
  pending: "이 탭에만 있는 변경사항 - 연결이 복구되면 전송됩니다",
  rejected: "서버가 거부한 변경사항이 이 탭에만 남아 있습니다",
};

const TERMINAL_TEXT: Readonly<Partial<Record<CollaborationCloseClassification, string>>> = {
  "authorization-downgraded": "이 문서에 대한 편집 권한이 읽기 전용으로 변경되었습니다.",
  "authorization-denied": "이 문서를 편집할 수 있는 권한이 없습니다.",
  "authorization-revoked": "이 문서에 대한 접근 권한이 회수되었습니다.",
  "lease-expired": "인증이 만료되어 편집을 중지했습니다.",
  "section-unavailable": "이 편집 영역에 접근할 수 없어 편집을 중지했습니다.",
  "room-limit-exceeded": "동시에 열 수 있는 편집 영역을 초과해 이 영역의 편집을 중지했습니다.",
};

const DEFAULT_TERMINAL_TEXT = "이 문서를 더 이상 편집할 수 없습니다.";
const RECOVERY_TEXT = "이 탭에만 남은 변경사항을 내보낼 수 있습니다.";

/**
 * Persistent text for one axis. A component that renders a single axis takes
 * only that axis: asking it for a whole `CollaborationEditorStatus` would force
 * it to invent filler values for the axes it does not observe, and those fillers
 * read as real state.
 */
export function collaborationTransportText(transport: CollaborationTransportState): string {
  return TRANSPORT_TEXT[transport];
}

export function collaborationLocalChangeText(localChanges: CollaborationLocalChangeState): string {
  return LOCAL_CHANGE_TEXT[localChanges];
}

/** Persistent text for each axis; never color or icon alone. */
export function collaborationStatusText(status: CollaborationEditorStatus): {
  transport: string;
  localChanges: string;
} {
  return {
    transport: collaborationTransportText(status.transport),
    localChanges: collaborationLocalChangeText(status.localChanges),
  };
}

/**
 * One polite line for the transport and local-change axes.
 *
 * Equal states produce an equal string, which is what deduplicates the
 * announcements: a live region only speaks when its text actually changes.
 */
export function collaborationAnnouncement(status: CollaborationEditorStatus): string {
  const text = collaborationStatusText(status);
  return `${text.transport}. ${text.localChanges}.`;
}

/**
 * Why access ended, plus - once - the fact that recovery is available. Both
 * belong to the same alert so a screen reader hears one terminal announcement
 * rather than two competing ones.
 */
export function collaborationTerminalText(
  classification: CollaborationCloseClassification | null,
  isRecoveryAvailable: boolean
): string {
  const reason = (classification && TERMINAL_TEXT[classification]) ?? DEFAULT_TERMINAL_TEXT;
  return isRecoveryAvailable ? `${reason} ${RECOVERY_TEXT}` : reason;
}

/** The single assertive announcement for a terminal authorization answer. */
export function collaborationTerminalAlert(state: CollaborationEditorState): string | null {
  if (!state.terminal) return null;
  return collaborationTerminalText(state.terminal.classification, state.isRecoveryAvailable);
}

/**
 * Best-effort "you would lose this tab's changes" prompt.
 *
 * Registered only while something is actually at risk so a clean document never
 * costs the user an extra click, and removed on sync or recovery resolution.
 */
export function useUnloadWarning(active: boolean): void {
  useEffect(() => {
    if (!active || typeof window === "undefined") return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Modern browsers ignore the string, but returnValue is what still arms
      // the prompt in the ones that do not.
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [active]);
}

/**
 * Editability that survives the IME.
 *
 * Taking `contenteditable` away in the middle of a 한글 composition makes the
 * browser drop the syllable being formed, so a room that loses authorization
 * while the user is composing stays open until `compositionend`. Network
 * admission is already blocked by then, so the finished text only reaches the
 * local Y.Doc, where recovery can still read it.
 */
export function useCompositionDeferredEditable(canEdit: boolean): {
  isEditable: boolean;
  onCompositionStart: () => void;
  onCompositionEnd: () => void;
} {
  const [isComposing, setIsComposing] = useState(false);
  const canEditRef = useLatestRef(canEdit);
  const wasEditableAtCompositionStart = useRef(false);

  return {
    isEditable: canEdit || (isComposing && wasEditableAtCompositionStart.current),
    onCompositionStart: () => {
      wasEditableAtCompositionStart.current = canEditRef.current;
      setIsComposing(true);
    },
    onCompositionEnd: () => setIsComposing(false),
  };
}

/**
 * Keeps a value readable from an event handler that must not be rebuilt.
 *
 * Composition and close handlers run outside React's render, and both have to
 * see the state as of the moment the DOM event fired.
 */
export function useLatestRef<T>(value: T): { readonly current: T } {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}
